import type { IHeadlinePromoPageConfig } from '@/modules/promotions/templates/HeadlinePromoTemplate/helpers';

const LVLS_IMAGE_PATH = '/headline-promo/solarpass/lvls';

const BG_STAGE_0 = {
  desk: '/headline-promo/solarpass/bg/bg-desk-0.webp',
  mob: '/headline-promo/solarpass/bg/bg-mob-0.webp',
};
const BG_STAGE_1 = {
  desk: '/headline-promo/solarpass/bg/bg-desk-1.webp',
  mob: '/headline-promo/solarpass/bg/bg-mob-1.webp',
};
const BG_STAGE_2 = {
  desk: '/headline-promo/solarpass/bg/bg-desk-2.webp',
  mob: '/headline-promo/solarpass/bg/bg-mob-2.webp',
};
const BG_STAGE_3 = {
  desk: '/headline-promo/solarpass/bg/bg-desk-3.webp',
  mob: '/headline-promo/solarpass/bg/bg-mob-3.webp',
};

const GLOW_STAGE_0 = '37, 170, 214';
const GLOW_STAGE_1 = '240, 192, 24';
const GLOW_STAGE_2 = '234, 91, 12';
const GLOW_STAGE_3 = '26, 178, 50';

const CHARACTER_IMAGE = {
  desk: '/headline-promo/solarpass/main_img.webp',
  mob: '/headline-promo/solarpass/main_img_mob.webp',
};

export const SOLAR_PASS_CONFIG: IHeadlinePromoPageConfig = {
  promoKey: 'solarpass',
  route: '/solarpass',
  namespace: 'headlinePromo',
  groupPrefix: 'SolarCore',
  hideHowToDescription: true,

  cardHud: {
    kind: 'svg',
    viewBox: '0 0 120.228 38.2',
    fillPath: 'M119.628 2.83534H2.57222V37.6H103.85L119.628 19.7177V2.83534Z',
    strokePath:
      'M0.6 18.4823V0.6L35.1139 0.6M2.57222 2.83534H119.628V19.7177L103.85 37.6H2.57222V2.83534Z',
  },

  // Locked cards and backgrounds share one image per level bracket: 1-3, 4-6, 7-9, 10
  levelsConfig: [
    {
      name: 'Spark',
      image: `${LVLS_IMAGE_PATH}/lvl-1.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-1-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 0, 25: 25, 50: 50, 75: 75, 100: 100 },
      reward: { kind: 'freespins', count: 10 },
      rewardGroupKey: 'solarpass_l1_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_STAGE_0,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Pulse',
      image: `${LVLS_IMAGE_PATH}/lvl-2.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-1-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 100, 25: 137.5, 50: 175, 75: 212.5, 100: 250 },
      reward: { kind: 'freespins', count: 15 },
      rewardGroupKey: 'solarpass_l2_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_STAGE_0,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Charge',
      image: `${LVLS_IMAGE_PATH}/lvl-3.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-1-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 250, 25: 312.5, 50: 375, 75: 437.5, 100: 500 },
      reward: { kind: 'freespins', count: 20 },
      rewardGroupKey: 'solarpass_l3_reward',
      bg: BG_STAGE_0,
      glowColor: GLOW_STAGE_0,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Core',
      image: `${LVLS_IMAGE_PATH}/lvl-4.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-2-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 500, 25: 625, 50: 750, 75: 875, 100: 1000 },
      reward: { kind: 'freespins', count: 30 },
      rewardGroupKey: 'solarpass_l4_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_STAGE_1,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Reactor',
      image: `${LVLS_IMAGE_PATH}/lvl-5.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-2-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 1000, 25: 1250, 50: 1500, 75: 1750, 100: 2000 },
      reward: { kind: 'freespins', count: 40 },
      rewardGroupKey: 'solarpass_l5_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_STAGE_1,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Surge',
      image: `${LVLS_IMAGE_PATH}/lvl-6.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-2-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 2000, 25: 2500, 50: 3000, 75: 3500, 100: 4000 },
      reward: { kind: 'freespins', count: 60 },
      rewardGroupKey: 'solarpass_l6_reward',
      bg: BG_STAGE_1,
      glowColor: GLOW_STAGE_1,
      characterImage: CHARACTER_IMAGE,
    },
    {
      name: 'Rift',
      image: `${LVLS_IMAGE_PATH}/lvl-7.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-3-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 4000, 25: 5000, 50: 6000, 75: 7000, 100: 8000 },
      reward: { kind: 'bonus', amount: 30 },
      rewardGroupKey: 'solarpass_l7_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_STAGE_2,
      characterImage: CHARACTER_IMAGE,
      artOverflows: true,
    },
    {
      name: 'Nova',
      image: `${LVLS_IMAGE_PATH}/lvl-9.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-3-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: { 0: 8000, 25: 9750, 50: 11_500, 75: 13_250, 100: 15_000 },
      reward: { kind: 'freespins', count: 40 },
      rewardGroupKey: 'solarpass_l8_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_STAGE_2,
      characterImage: CHARACTER_IMAGE,
      artOverflows: true,
    },
    {
      name: 'Apex',
      image: `${LVLS_IMAGE_PATH}/lvl-8.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-3-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: {
        0: 15_000,
        25: 18_750,
        50: 22_500,
        75: 26_250,
        100: 30_000,
      },
      reward: { kind: 'bonus', amount: 50 },
      rewardGroupKey: 'solarpass_l9_reward',
      bg: BG_STAGE_2,
      glowColor: GLOW_STAGE_2,
      characterImage: CHARACTER_IMAGE,
      artOverflows: true,
    },
    {
      name: 'Ascendant',
      image: `${LVLS_IMAGE_PATH}/lvl-10.webp`,
      lockedImage: `${LVLS_IMAGE_PATH}/stage-4-locked.webp`,
      promoRewardType: 'bets',
      wagerAmounts: {
        0: 30_000,
        25: 37_500,
        50: 45_000,
        75: 52_500,
        100: 60_000,
      },
      reward: { kind: 'bonus', amount: 100 },
      rewardGroupKey: 'solarpass_l10_reward',
      bg: BG_STAGE_3,
      glowColor: GLOW_STAGE_3,
      characterImage: CHARACTER_IMAGE,
      artOverflows: true,
    },
  ],
};
